import { PropsWithChildren, useEffect, useState } from "react";
import {
  Popover,
  PopoverAlignment,
  PopoverAnchor,
  PopoverContent,
  PopoverDirection,
} from "../popover/popover";
import { useTranslation } from "react-i18next";
import { AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { ResponseError } from "@/lib/errors";

export interface ErrorPopoverProps {
  error?: Error | null;
  title?: string;
  description?: string;
  options?: {
    align?: PopoverAlignment;
    direction?: PopoverDirection;
  };
  className?: string;
}

export function ErrorPopover({
  error,
  title,
  description,
  options,
  className,
  children,
}: PropsWithChildren<ErrorPopoverProps>) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (error) {
      setOpen(true);
    }
  }, [error]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>{children}</PopoverAnchor>
      <PopoverContent
        align={options?.align}
        side={options?.direction ?? "top"}
        sideOffset={10}
        className={cn(
          "border-0 bg-destructive-muted text-destructive text-sm p-3 w-min min-w-[var(--radix-popover-trigger-width)] max-w-[600px]",
          className
        )}
      >
        <div className="flex items-center gap-2">
          <span>
            <AlertCircle className="w-6 h-6" />
          </span>
          <div className="flex flex-col gap-1 w-[90%] break-words">
            <p className="font-semibold">{title || t("errors.message")}</p>
            <p>
              {error instanceof ResponseError && error.code
                ? `[${error.code}] `
                : null}
              {description || error?.message}
            </p>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
